import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useDeleteCommissionsLevelRule } from "../../../../../../common/api/queries";
import ActionTextIcon from "../../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../../common/components/icons/PopconfirmDeleteIcon";
import { PageSizes, TableSizes } from "../../../../../../common/constants";
import { CommissionSourceTag } from "../../../../settings/components/CommissionSourceTag";
import { CommissionsSettingsRuleView } from "../../../../settings/components/views/CommissionsSettingsRuleView";
import { sortCommissionsSettingsRules } from "../../../../settings/utils";
import { CommissionsSettingsLevelType } from "../../../enums";
import { CommissionsSettingsLevel, CommissionsSettingsLevelRule } from "../../../types";

interface Props {
  level: CommissionsSettingsLevel;
  showActions: boolean;
  onUpdateClick: (rule: CommissionsSettingsLevelRule) => void;
}

export const CommissionsLevelRuleTableView = ({ level, showActions, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const deleteCommissionsLevelRule = useDeleteCommissionsLevelRule();

  const getColumns = () => {
    const columns: ColumnsType<CommissionsSettingsLevelRule> = [
      {
        key: "rule",
        title: t("commissions.level.sections.rule"),
        render: (_, record) => <CommissionsSettingsRuleView rule={record} />,
      },
    ];

    if (level.type === CommissionsSettingsLevelType.CAREER) {
      columns.push({
        key: "commissionAmountPerPoint",
        title: t("commissions.level.attrs.rule.commissionAmountPerPoint"),
        width: 200,
        align: "right",
        render: (_, record) =>
          isDefined(record.commissionAmountPerPoint)
            ? formatLocaleCurrency(record.commissionAmountPerPoint)
            : undefined,
      });
    } else {
      columns.push(
        {
          key: "source",
          title: t("commissions.settings.enums.commissionSource._label"),
          width: 115,
          render: (_, record) =>
            record.source ? <CommissionSourceTag source={record.source} style={{ marginRight: 0 }} /> : undefined,
        },
        {
          key: "initialCommissionRate",
          title: t("commissions.level.attrs.rule.initialCommissionRate"),
          width: 120,
          align: "right",
          render: (_, record) =>
            isDefined(record.initialCommissionRate)
              ? formatLocalePercentageNumber(record.initialCommissionRate)
              : undefined,
        },
        {
          key: "subsequentCommissionRate",
          title: t("commissions.level.attrs.rule.subsequentCommissionRate"),
          width: 110,
          align: "right",
          render: (_, record) =>
            isDefined(record.subsequentCommissionRate)
              ? formatLocalePercentageNumber(record.subsequentCommissionRate)
              : undefined,
        },
      );
    }

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <div className="nowrap">
            <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("commissions.level.helpers.deleteRuleConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() => deleteCommissionsLevelRule.mutate({ id1: level.id, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </div>
        ),
      });
    }

    return columns;
  };

  return (
    <Table<CommissionsSettingsLevelRule>
      {...tableStandardProps()}
      columns={getColumns()}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={sortCommissionsSettingsRules(level.rules)}
      pagination={{
        ...paginationTableProps,
        pageSize: PageSizes.SMALL,
        total: level.rules?.length,
      }}
    />
  );
};
